import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { STORE_URL_HEADER } from "@redotech/shopify-sdk/rpc/custom-http-headers";
import { createContext, memo, ReactNode, useContext } from "react";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { ZipCodeContext } from "./zip-code-context";

interface DateEstimateContextValue {
  estimatedDeliveryDate: Date | undefined;
  isLoading: boolean;
}

export const DateEstimateContext = createContext<DateEstimateContextValue>({
  estimatedDeliveryDate: undefined,
  isLoading: false,
});

export const DateEstimateProvider = memo(function DateEstimateProvider({
  children,
  storeUrl,
}: {
  children: ReactNode | ReactNode[];
  storeUrl: string;
}) {
  const shopifyRpcClient = useRequiredContext(RedoShopifyRpcClientContext);
  const zipContext = useContext(ZipCodeContext);

  const estimateLoad = useLoad(async () => {
    if (!zipContext?.savedZipCode || !storeUrl) {
      return;
    }

    const response = await shopifyRpcClient.getDeliveryEstimate(
      {
        zipCode: zipContext.savedZipCode,
        storeUrl_forCacheRoutingOnly: storeUrl,
      },
      { method: "GET", requestHeaders: { [STORE_URL_HEADER]: storeUrl } },
    );

    return response.estimatedDeliveryDate;
  }, [zipContext?.savedZipCode, storeUrl]);

  const dateEstimateState: DateEstimateContextValue = {
    estimatedDeliveryDate: estimateLoad.value,
    isLoading: estimateLoad.pending,
  };

  return (
    <DateEstimateContext.Provider value={dateEstimateState}>
      {children}
    </DateEstimateContext.Provider>
  );
});
